import React from "react";
import { Link } from "@inertiajs/react";
import styled from "styled-components";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  outerContainerCss,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import CardActionsOverlay from "../../homes/CardActionsOverlay";

function firstLessonUrl(curriculum) {
  if (curriculum.lessons && curriculum.lessons.length > 0) {
    return curriculum.lessons[0].detail_url;
  }
  if (curriculum.lessons_grouped && curriculum.lessons_grouped.length > 0) {
    const flat = curriculum.lessons_grouped.flatMap((g) => g.lessons || []);
    if (flat.length > 0) return flat[0].detail_url;
  }
  return null;
}

function CurriculumHero({ curriculum }) {
  if (!curriculum) return null;
  const continueUrl = curriculum.continue_lesson?.detail_url;
  const startUrl = continueUrl || firstLessonUrl(curriculum);
  const ctaLabel = continueUrl ? "Lanjut Menonton →" : "Mulai Materi Pertama →";
  const hasProgress =
    typeof curriculum.curriculum_progress_percent === "number";

  return (
    <Band>
      <Inner>
        <BannerWrap data-card-host tabIndex={0}>
          {curriculum.banner_image_url ? (
            <BannerImg
              src={curriculum.banner_image_url}
              alt={curriculum.name}
              loading="eager"
            />
          ) : (
            <BannerPlaceholder aria-hidden="true">
              {curriculum.name?.[0] || "K"}
            </BannerPlaceholder>
          )}
          <CardActionsOverlay
            resourceType="Curriculum"
            resourceId={curriculum.id}
            detailLink={null}
            showDetail={false}
            initialLiked={!!curriculum.is_liked}
            initialDisliked={!!curriculum.is_disliked}
            initialBookmarked={!!curriculum.is_bookmarked}
            radius="20px"
          />
        </BannerWrap>
        <Body>
          <CourseLabel>{curriculum.course?.name}</CourseLabel>
          <Title>{curriculum.name}</Title>
          {curriculum.description && (
            <Tagline>{curriculum.description}</Tagline>
          )}
          <StatsRow>
            <StatTile>
              <StatValue>{curriculum.lessons?.length || 0}</StatValue>
              <StatLabel>Materi</StatLabel>
            </StatTile>
            {curriculum.exam?.available && (
              <StatTile>
                <StatValue>{curriculum.exam.total_questions || 0}</StatValue>
                <StatLabel>Pertanyaan Ujian</StatLabel>
              </StatTile>
            )}
            {hasProgress && (
              <StatTile $emphasis>
                <StatValue $emphasis>
                  {curriculum.curriculum_progress_percent}%
                </StatValue>
                <StatLabel>Progres</StatLabel>
              </StatTile>
            )}
          </StatsRow>
          {hasProgress && (
            <Progress>
              <Bar
                role="progressbar"
                aria-valuenow={curriculum.curriculum_progress_percent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <Fill
                  style={{
                    width: `${curriculum.curriculum_progress_percent}%`,
                  }}
                />
              </Bar>
            </Progress>
          )}
          {startUrl && (
            <CtaRow>
              <PrimaryCta href={startUrl}>{ctaLabel}</PrimaryCta>
            </CtaRow>
          )}
        </Body>
      </Inner>
    </Band>
  );
}

export default React.memo(CurriculumHero);

const Band = styled.section`
  background:
    radial-gradient(
      120% 80% at 12% 0%,
      rgba(255, 255, 255, 0.9),
      transparent 55%
    ),
    linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  padding: 64px 0;
  border-bottom: 1px solid rgba(216, 89, 7, 0.18);

  @media ${narrowScreenMediaQuery} {
    padding: 36px 0;
  }

  [data-theme="dark"] & {
    background:
      radial-gradient(
        120% 80% at 12% 0%,
        rgba(255, 255, 255, 0.04),
        transparent 55%
      ),
      linear-gradient(135deg, #1f2937 0%, #0f172a 100%);
    border-bottom-color: rgba(216, 89, 7, 0.32);
  }
`;

const Inner = styled.div`
  ${outerContainerCss}
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 44px;
  align-items: center;

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    gap: 22px;
  }
`;

const BannerWrap = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(135deg, #fcd34d, #fb923c);
  box-shadow:
    ${card_shadow_md},
    0 0 0 1px rgba(216, 89, 7, 0.18);

  [data-theme="dark"] & {
    box-shadow:
      ${card_shadow_md},
      0 0 0 1px rgba(216, 89, 7, 0.32);
  }
`;

const BannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const BannerPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 88px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.85);
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const CourseLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

const Title = styled.h1`
  font-size: 40px;
  font-weight: 800;
  margin: 0;
  color: ${brand_orange_deep};
  line-height: 1.18;
  letter-spacing: -0.01em;
  @media ${narrowScreenMediaQuery} {
    font-size: 26px;
  }
`;

const Tagline = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.65;
  color: #4b5563;
  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 18px;
  border-radius: 12px;
  border: 1px solid
    ${({ $emphasis }) =>
      $emphasis ? "rgba(216, 89, 7, 0.32)" : "rgba(15, 23, 42, 0.08)"};
  background: ${({ $emphasis }) =>
    $emphasis
      ? "linear-gradient(135deg, rgba(216, 89, 7, 0.10), rgba(247, 179, 12, 0.08))"
      : "rgba(255, 255, 255, 0.6)"};
  min-width: 96px;

  [data-theme="dark"] & {
    background: ${({ $emphasis }) =>
      $emphasis ? "rgba(216, 89, 7, 0.18)" : "rgba(255, 255, 255, 0.04)"};
    border-color: ${({ $emphasis }) =>
      $emphasis ? "rgba(216, 89, 7, 0.45)" : "rgba(255, 255, 255, 0.10)"};
  }
`;

const StatValue = styled.span`
  font-size: 22px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${({ $emphasis }) => ($emphasis ? brand_orange_deep : "#1f2937")};
  [data-theme="dark"] & {
    color: ${({ $emphasis }) => ($emphasis ? "#fdba74" : "#f3f4f6")};
  }
`;

const StatLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: #6b7280;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const Progress = styled.div`
  margin-top: 2px;
`;

const Bar = styled.div`
  height: 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  overflow: hidden;
  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const Fill = styled.div`
  height: 100%;
  background: ${brand_gradient};
`;

const CtaRow = styled.div`
  margin-top: 4px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const PrimaryCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 14px 28px;
  border-radius: 999px;
  background: ${brand_orange_deep};
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-decoration: none;
  transition:
    filter 150ms ease,
    transform 100ms ease;

  &:hover {
    filter: brightness(0.95);
    transform: translateY(-1px);
    color: #fff;
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
`;
